import { BankAccount } from "@/types";
import { Ref } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  BankAccountFormInput,
  BankAccountValidationSchema,
} from "@/components/pages/Settings/validation/bankAccountValidationSchema";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { postNewBankAccount, updateBankAccount } from "@/services/Settings";
import { Grid } from "@mantine/core";
import { useTranslation } from "react-i18next";
import TextInput from "@/components/atoms/TextInput";

interface BankAccountStepProps {
  onValidationSuccess: () => void;
  bankAccount?: BankAccount;
  companyId: number;
  ref?: Ref<HTMLFormElement>;
}

function BankAccountStep({ onValidationSuccess, bankAccount, companyId, ref }: BankAccountStepProps) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { handleSubmit, control } = useForm<BankAccountFormInput>({
    resolver: zodResolver(BankAccountValidationSchema),
    mode: "onBlur",
    reValidateMode: "onChange",
    values: {
      name: bankAccount?.name ?? "",
      accountNo: bankAccount?.accountNo ?? "",
      iban: bankAccount?.iban ?? "",
    },
  });

  const { mutateAsync: editBankAccount } = useMutation({
    mutationFn: async ({ name, accountNo, iban }: BankAccountFormInput) => {
      if (!bankAccount) {
        console.error("Error: Bank account or its ID is missing or invalid.");
        return;
      }
      await updateBankAccount(companyId, bankAccount.id, name, accountNo.trim(), iban.trim());
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["getCompanyById", companyId],
      });
      onValidationSuccess();
    },
    onError: (error) => {
      console.error("Error updating bank account information:", error);
    },
  });

  const { mutateAsync: createBankAccount } = useMutation({
    mutationFn: async ({ name, accountNo, iban }: BankAccountFormInput) => {
      await postNewBankAccount(companyId, name, accountNo.trim(), iban.trim());
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["getCompanyById", companyId],
      });
      onValidationSuccess();
    },
    onError: (error) => {
      console.error(error);
    },
  });

  const onSubmit = async (data: BankAccountFormInput) => {
    if (!bankAccount) {
      await createBankAccount({
        name: data.name,
        accountNo: data.accountNo,
        iban: data.iban,
      });
    } else {
      await editBankAccount({
        name: data.name,
        accountNo: data.accountNo,
        iban: data.iban,
      });
    }
  };

  return (
    <form ref={ref} onSubmit={handleSubmit(onSubmit)}>
      <Grid>
        <Grid.Col span={{ base: 12, lg: 6 }}>
          <Controller
            control={control}
            name="name"
            render={({ field, fieldState }) => (
              <TextInput
                {...field}
                required
                label={t("settings:bankAccountName")}
                error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
              />
            )}
          />
        </Grid.Col>

        <Grid.Col span={{ base: 12, lg: 6 }}>
          <Controller
            control={control}
            name="accountNo"
            render={({ field, fieldState }) => (
              <TextInput
                {...field}
                required
                label={t("settings:bankAccountNr")}
                error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
              />
            )}
          />
        </Grid.Col>

        <Grid.Col span={{ base: 12, lg: 6 }}>
          <Controller
            control={control}
            name="iban"
            render={({ field, fieldState }) => (
              <TextInput
                {...field}
                required
                label={"IBAN"}
                error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
              />
            )}
          />
        </Grid.Col>
      </Grid>
    </form>
  );
}

export default BankAccountStep;
